import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import { AppServices } from 'middleware'
import { endpoints as articleEndpoints, invalidatePLCache } from 'products/reduxApi'
import { createQueryResponseHandling } from 'utils/errorHandler'
import { hasFeature } from 'features/hocs'
import { State } from 'store'
import { CapacityDetails, CapacitySummary } from './types'
import { CapacityMembers, SavePlan, SaveResult, TodayUsage } from './api/capacityGateway'
import { OptionItemSweep, sweepOptionItems } from './api/clientSideGateway'
import { CAPACITY_SERVER_FLAGS, CapacityServerFlags, createCapacityGateway } from './api/createCapacityGateway'

export const capacityApi = createApi({
    reducerPath: 'capacityApi',
    tagTypes: ['Capacities', 'Capacity', 'CapacityMembers', 'TodayUsage', 'OptionItemSweep'],
    baseQuery: fetchBaseQuery({}),
    endpoints: () => ({}),
})

export const serverFlagsFromState = (state: State, accountSlug: string): CapacityServerFlags => {
    const conviousAdmin = state.auth.user?.isAdmin ?? false
    const read = (featureName: string) =>
        hasFeature({ accountSlug, features: state.features, featureName, conviousAdmin })
    return {
        members: read(CAPACITY_SERVER_FLAGS.members),
        save: read(CAPACITY_SERVER_FLAGS.save),
        delete: read(CAPACITY_SERVER_FLAGS.delete),
        health: read(CAPACITY_SERVER_FLAGS.health),
        usage: read(CAPACITY_SERVER_FLAGS.usage),
    }
}

type BaseQueryApi = Parameters<BaseQueryFn>[1]

const optionItemsApi = capacityApi.injectEndpoints({
    endpoints: (builder) => ({
        getOptionItemSweep: builder.query<OptionItemSweep, string>({
            providesTags: ['OptionItemSweep'],
            keepUnusedDataFor: 300,
            queryFn: (accountSlug) =>
                createQueryResponseHandling(() => sweepOptionItems(AppServices.optionsService, accountSlug)),
        }),
    }),
})

const gatewayFor = (api: BaseQueryApi, accountSlug: string) =>
    createCapacityGateway(
        {
            inventoryService: AppServices.inventoryService,
            articleService: AppServices.articleService,
            capacityReportService: AppServices.capacityReportService,
            loadArticles: async (slug) => {
                const subscription = api.dispatch(articleEndpoints.getAllArticles.initiate({ account: slug }))
                try {
                    return await subscription.unwrap()
                } finally {
                    subscription.unsubscribe()
                }
            },
            loadOptionItems: async (slug) => {
                const subscription = api.dispatch(optionItemsApi.endpoints.getOptionItemSweep.initiate(slug))
                try {
                    return await subscription.unwrap()
                } finally {
                    subscription.unsubscribe()
                }
            },
        },
        serverFlagsFromState(api.getState() as State, accountSlug),
    )

const extendedApi = capacityApi.injectEndpoints({
    endpoints: (builder) => ({
        getCapacities: builder.query<CapacitySummary[], string>({
            providesTags: (result) => [
                'Capacities',
                ...(result ?? []).map((capacity) => ({ type: 'Capacity' as const, id: capacity.id })),
            ],
            queryFn: (accountSlug, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).listCapacities(accountSlug)),
        }),
        getCapacity: builder.query<CapacityDetails, { accountSlug: string; capacityId: string }>({
            providesTags: (_, __, { capacityId }) => [{ type: 'Capacity', id: capacityId }],
            queryFn: ({ accountSlug, capacityId }, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).getCapacity(accountSlug, capacityId)),
        }),
        getCapacityMembers: builder.query<CapacityMembers, { accountSlug: string; capacityId: string }>({
            providesTags: (_, __, { capacityId }) => [{ type: 'CapacityMembers', id: capacityId }],
            queryFn: ({ accountSlug, capacityId }, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).getMembers(accountSlug, capacityId)),
        }),
        getTodayUsage: builder.query<
            Record<string, TodayUsage>,
            { accountSlug: string; capacityIds: string[]; date: string }
        >({
            providesTags: (_, __, { capacityIds }) => capacityIds.map((id) => ({ type: 'TodayUsage' as const, id })),
            keepUnusedDataFor: 60,
            queryFn: ({ accountSlug, capacityIds, date }, api) =>
                createQueryResponseHandling(() =>
                    gatewayFor(api, accountSlug).getTodayUsage(accountSlug, capacityIds, date),
                ),
        }),
        saveCapacity: builder.mutation<SaveResult, { accountSlug: string; plan: SavePlan }>({
            invalidatesTags: (result) =>
                result?.capacityId
                    ? [
                          'Capacities',
                          { type: 'Capacity', id: result.capacityId },
                          { type: 'CapacityMembers', id: result.capacityId },
                          { type: 'TodayUsage', id: result.capacityId },
                      ]
                    : ['Capacities'],
            queryFn: async ({ accountSlug, plan }, api) => {
                const response = await createQueryResponseHandling(() =>
                    gatewayFor(api, accountSlug).save(accountSlug, plan),
                )
                if (response.data && plan.productOps.length > 0) {
                    invalidatePLCache(api.dispatch, accountSlug)
                }
                return response
            },
        }),
    }),
})

export const { reducer, reducerPath } = capacityApi
export const {
    useGetCapacitiesQuery,
    useGetCapacityQuery,
    useGetCapacityMembersQuery,
    useGetTodayUsageQuery,
    useSaveCapacityMutation,
} = extendedApi
